import {NavTree} from "./nav.tree";
import {NavItem} from "./nav.item";
import {Paths} from "../../paths";
import {PermissionScope} from "../../services/auth/permission-scope.enum";
import {KeyValueDto} from "../../repositories/dto/key-value.dto";
import {environment} from "../../../environments/environment";
import {Feature} from "../../libs/enum/feature.enum";

export class NavOrg {

  private static readonly isIronBackupViewer: boolean = environment.ironBackupViewer;
  private static readonly supportListEnabled: boolean = environment.support.enabled && environment.support.list;

  public static build(): NavTree {
    // ########################################################################
    // #### Overview
    // ########################################################################
    const overview = new NavItem({
      id: 'overview-main',
      title: 'overview.name',
      icon: 'dashboard',
      route: Paths.OVERVIEW,
      paths: [Paths.OVERVIEW],
    });

    // ########################################################################
    // #### Funeral
    // ########################################################################
    const funeral = new NavItem({
      id: 'funeral-main',
      title: 'funerals',
      icon: 'tombstone',
      route: Paths.FUNERAL_LIST,
      paths: [Paths.FUNERAL_LIST, Paths.FUNERAL_EDIT, Paths.FUNERAL_VIEW],
      permissions: [PermissionScope.FUNERAL]
    });
    funeral.children.push(new NavItem({
      id: 'registry-office',
      title: 'registryOffices',
      icon: 'building',
      route: Paths.REGISTRY_OFFICE_LIST,
      paths: [Paths.REGISTRY_OFFICE_LIST, Paths.REGISTRY_OFFICE_EDIT, Paths.REGISTRY_OFFICE_VIEW],
      parent: funeral,
      permissions: []
    }));

    // ########################################################################
    // #### Invoice
    // #### Is filtered by NavService.filterNavItemsBySettings (first steps)
    // ########################################################################
    const invoice = new NavItem({
      id: 'invoice-main',
      title: 'invoices',
      icon: 'invoice',
      reloadRoute: true,
      route: Paths.INVOICE_LIST,
      paths: [Paths.INVOICE_LIST, Paths.INVOICE_VIEW, Paths.INVOICE_EDIT],
      permissions: [PermissionScope.INVOICE]
    });
    // Invoice - Draft / Entwurf
    invoice.children.push(new NavItem({
      id: 'invoice-draft',
      title: 'invoice.drafts',
      icon: 'invoice-draft',
      reloadRoute: true,
      route: Paths.INVOICE_LIST,
      params: [new KeyValueDto({key: 'filterState', value: 'DRAFT'})],
      paths: [Paths.INVOICE_LIST, Paths.INVOICE_VIEW, Paths.INVOICE_EDIT],
      parent: invoice,
      permissions: [PermissionScope.INVOICE]
    }));
    // Invoice - Orders / Auftraege
    invoice.children.push(new NavItem({
      id: 'invoice-order',
      title: 'invoice.orders',
      icon: 'invoice-order',
      reloadRoute: true,
      route: Paths.INVOICE_LIST,
      params: [new KeyValueDto({key: 'filterState', value: 'ORDER'})],
      paths: [Paths.INVOICE_LIST, Paths.INVOICE_VIEW, Paths.INVOICE_EDIT],
      parent: invoice,
      permissions: [PermissionScope.INVOICE]
    }));
    // Invoice / Rechnungen
    const invoiceChild = new NavItem({
      id: 'invoice-list',
      title: 'invoices',
      icon: 'invoice',
      reloadRoute: true,
      route: Paths.INVOICE_LIST,
      params: [new KeyValueDto({key: 'filterState', value: 'INVOICE'})],
      paths: [Paths.INVOICE_LIST, Paths.INVOICE_VIEW, Paths.INVOICE_EDIT],
      parent: invoice,
      permissions: [PermissionScope.INVOICE]
    });
    invoice.children.push(invoiceChild);
    // Invoice-Sub - NOT paid
    invoiceChild.children.push(new NavItem({
      id: 'invoice-unpaid',
      title: 'payment.unpaid',
      icon: 'invoice',
      reloadRoute: true,
      route: Paths.INVOICE_LIST,
      params: [
        new KeyValueDto({key: 'filterState', value: 'INVOICE'}),
        new KeyValueDto({key: 'filterPayment', value: 'false'})
      ],
      paths: [Paths.INVOICE_LIST, Paths.INVOICE_VIEW, Paths.INVOICE_EDIT],
      parent: invoiceChild,
      permissions: [PermissionScope.INVOICE, PermissionScope.INVOICE_PAYMENT]
    }));
    // Invoice-Sub - Canceled
    invoiceChild.children.push(new NavItem({
      id: 'invoice-cancelled',
      title: 'invoice.cancelled',
      icon: 'invoice-cancellation',
      reloadRoute: true,
      route: Paths.INVOICE_LIST,
      params: [
        new KeyValueDto({key: 'filterState', value: 'INVOICE'}),
        new KeyValueDto({key: 'filterCancelled', value: 'true'})
      ],
      paths: [Paths.INVOICE_LIST, Paths.INVOICE_VIEW, Paths.INVOICE_EDIT],
      parent: invoiceChild,
      permissions: [PermissionScope.INVOICE, PermissionScope.INVOICE_CANCEL]
    }));
    // Invoice - Cost estimate / Kostenvoranschlag
    invoice.children.push(new NavItem({
      id: 'invoice-cost-estimate',
      title: 'invoice.costEstimates',
      icon: 'invoice-cost-estimate',
      reloadRoute: true,
      route: Paths.INVOICE_LIST,
      params: [new KeyValueDto({key: 'filterState', value: 'COST_ESTIMATE'})],
      paths: [Paths.INVOICE_LIST, Paths.INVOICE_VIEW, Paths.INVOICE_EDIT],
      parent: invoice,
      permissions: [PermissionScope.INVOICE]
    }));
    // Invoice - Cancellations
    invoice.children.push(new NavItem({
      id: 'invoice-cancellation',
      title: 'invoice.cancellations',
      icon: 'invoice-cancellation',
      reloadRoute: true,
      route: Paths.INVOICE_LIST,
      params: [new KeyValueDto({key: 'filterState', value: 'CANCELLATION'})],
      paths: [Paths.INVOICE_LIST, Paths.INVOICE_VIEW, Paths.INVOICE_EDIT],
      parent: invoice,
      permissions: [PermissionScope.INVOICE]
    }));

    // ########################################################################
    // #### Receipt
    // ########################################################################
    const receipt = new NavItem({
      id: 'receipt-main',
      title: 'receipts',
      icon: 'receipt',
      reloadRoute: true,
      route: Paths.RECEIPT_LIST,
      paths: [Paths.RECEIPT_LIST, Paths.RECEIPT_VIEW, Paths.RECEIPT_EDIT],
      permissions: [PermissionScope.RECEIPT]
    });
    // Receipt - NOT reviewed
    receipt.children.push(new NavItem({
      id: 'receipt-unreviewed',
      title: 'receipt.notReviewed',
      icon: 'receipt-unreviewed',
      reloadRoute: true,
      route: Paths.RECEIPT_LIST,
      params: [new KeyValueDto({key: 'filterReviewed', value: 'false'})],
      paths: [Paths.RECEIPT_LIST, Paths.RECEIPT_VIEW, Paths.RECEIPT_EDIT],
      parent: receipt,
      permissions: [PermissionScope.RECEIPT]
    }));
    // Receipt - NOT paid
    receipt.children.push(new NavItem({
      id: 'receipt-unpaid',
      title: 'payment.unpaid',
      icon: 'receipt',
      reloadRoute: true,
      route: Paths.RECEIPT_LIST,
      params: [new KeyValueDto({key: 'filterPayment', value: 'false'})],
      paths: [Paths.RECEIPT_LIST, Paths.RECEIPT_VIEW, Paths.RECEIPT_EDIT],
      parent: receipt,
      permissions: [PermissionScope.RECEIPT, PermissionScope.RECEIPT_PAYMENT]
    }));

    // ########################################################################
    // #### Contact
    // ########################################################################
    const contact = new NavItem({
      id: 'contact-main',
      title: 'contacts',
      icon: 'contact',
      reloadRoute: true,
      route: Paths.CONTACT_LIST,
      paths: [
        Paths.CONTACT_LIST, Paths.CONTACT_VIEW, Paths.CONTACT_EDIT,
        Paths.CONTACT_RELATION_VIEW, Paths.CONTACT_RELATION_EDIT
      ],
      permissions: [PermissionScope.CONTACT]
    });
    // Contact - Debtor
    contact.children.push(new NavItem({
      id: 'contact-debtor',
      title: 'debtor',
      icon: 'contact',
      route: Paths.CONTACT_LIST,
      params: [
        new KeyValueDto({key: 'category', value: 'DEBTOR'}),
        new KeyValueDto({key: 'filterCategory', value: 'DEBTOR'})
      ],
      paths: [Paths.CONTACT_LIST, Paths.CONTACT_VIEW, Paths.CONTACT_EDIT],
      parent: contact,
      permissions: [PermissionScope.CONTACT]
    }));
    // Contact - Creditor
    contact.children.push(new NavItem({
      id: 'contact-creditor',
      title: 'creditor',
      icon: 'contact',
      route: Paths.CONTACT_LIST,
      params: [
        new KeyValueDto({key: 'category', value: 'CREDITOR'}),
        new KeyValueDto({key: 'filterCategory', value: 'CREDITOR'})
      ],
      paths: [Paths.CONTACT_LIST, Paths.CONTACT_VIEW, Paths.CONTACT_EDIT],
      parent: contact,
      permissions: [PermissionScope.CONTACT]
    }));
    // Contact - Employee
    contact.children.push(new NavItem({
      id: 'contact-employee',
      title: 'employee',
      icon: 'contact',
      route: Paths.CONTACT_LIST,
      params: [
        new KeyValueDto({key: 'category', value: 'EMPLOYEE'}),
        new KeyValueDto({key: 'filterCategory', value: 'EMPLOYEE'})
      ],
      paths: [Paths.CONTACT_LIST, Paths.CONTACT_VIEW, Paths.CONTACT_EDIT],
      parent: contact,
      permissions: [PermissionScope.CONTACT, PermissionScope.CONTACT_EMPLOYEE]
    }));
    // Contact - Person (CONTACT_RELATION)
    contact.children.push(new NavItem({
      id: 'contact-person',
      title: 'persons',
      icon: 'contact-person',
      route: Paths.CONTACT_LIST,
      params: [
        new KeyValueDto({key: 'category', value: 'PERSON'}),
        new KeyValueDto({key: 'filterCategory', value: 'PERSON'}),
      ],
      paths: [Paths.CONTACT_LIST, Paths.CONTACT_VIEW, Paths.CONTACT_EDIT],
      parent: contact,
      permissions: [PermissionScope.CONTACT],
      features: [Feature.CONTACT_RELATION]
    }));
    // Contact - Medical
    contact.children.push(new NavItem({
      id: 'contact-medical',
      title: 'contact.medical',
      icon: 'medical-house',
      route: Paths.CONTACT_LIST,
      params: [new KeyValueDto({key: 'filterCategory', value: 'MEDICAL'})],
      paths: [Paths.CONTACT_LIST, Paths.CONTACT_VIEW, Paths.CONTACT_EDIT],
      parent: contact,
      permissions: [PermissionScope.CONTACT],
      features: [Feature.FUNERAL_EXTENDED]
    }));

    // ########################################################################
    // #### Contact - Vehicle (Customer-Vehicle)
    // ########################################################################
    contact.children.push(new NavItem({
      id: 'contact-vehicle',
      title: 'contact.vehicles.nav',
      icon: 'vehicle',
      route: Paths.CONTACT_VEHICLE_LIST,
      paths: [
        Paths.CONTACT_VEHICLE_LIST, Paths.CONTACT_VEHICLE_VIEW, Paths.CONTACT_VEHICLE_EDIT,
        Paths.VEHICLE_HISTORY_VIEW, Paths.VEHICLE_HISTORY_EDIT,
        Paths.VEHICLE_REPAIR_VIEW, Paths.VEHICLE_REPAIR_EDIT,
        Paths.VEHICLE_TIRE_VIEW, Paths.VEHICLE_TIRE_EDIT
      ],
      parent: contact,
      permissions: [PermissionScope.CONTACT_VEHICLE]
    }));
    // Contact - Vehicle - Inspection
    contact.children.push(new NavItem({
      id: 'contact-vehicle-inspection',
      title: 'vehicle.inspection',
      icon: 'vehicle-inspection',
      route: Paths.CONTACT_VEHICLE_INSPECTION,
      params: [KeyValueDto.of('inspectionFilter', 'true')],
      paths: [Paths.CONTACT_VEHICLE_INSPECTION],
      parent: contact,
      permissions: [PermissionScope.CONTACT_VEHICLE]
    }));
    // Contact - Vehicle - Tire-Change
    contact.children.push(new NavItem({
      id: 'contact-tire-change',
      title: 'vehicle.tireChange',
      icon: 'vehicle-tire',
      route: Paths.CONTACT_VEHICLE_TIRES,
      params: [KeyValueDto.of('tireChangeFilter', 'true')],
      paths: [Paths.CONTACT_VEHICLE_TIRES],
      parent: contact,
      permissions: [PermissionScope.CONTACT_VEHICLE]
    }));
    // Contact - Sheet / Briefe / Anschreiben
    contact.children.push(new NavItem({
      id: 'sheet',
      title: 'sheets',
      icon: 'sheet',
      route: Paths.SHEET_LIST,
      paths: [Paths.SHEET_LIST, Paths.SHEET_VIEW, Paths.SHEET_EDIT],
      parent: contact,
      permissions: [PermissionScope.DOCUMENT],
      features: [Feature.DOCUMENT]
    }));

    // ########################################################################
    // #### Article
    // ########################################################################
    const article = new NavItem({
      id: 'article-main',
      title: 'articles',
      icon: 'article',
      route: Paths.ARTICLE_LIST,
      paths: [Paths.ARTICLE_LIST, Paths.ARTICLE_VIEW, Paths.ARTICLE_EDIT],
      permissions: [PermissionScope.ARTICLE]
    });
    // Article - Catalog
    article.children.push(new NavItem({
      id: 'article-catalog',
      title: 'article.catalogs',
      icon: 'catalog',
      route: Paths.CATALOG_LIST,
      paths: [Paths.CATALOG_LIST, Paths.CATALOG_VIEW, Paths.CATALOG_EDIT],
      parent: article,
      permissions: [PermissionScope.CATALOG]
    }));
    // Article - Goods-Group
    article.children.push(new NavItem({
      id: 'article-goods-group',
      title: 'article.goodsGroups',
      icon: 'goods-group',
      route: Paths.GOODS_GROUP_LIST,
      paths: [Paths.GOODS_GROUP_LIST, Paths.GOODS_GROUP_VIEW, Paths.GOODS_GROUP_EDIT],
      parent: article,
      permissions: [PermissionScope.ARTICLE],
      features: [Feature.ARTICLE]
    }));

    // ########################################################################
    // #### Calculation
    // ########################################################################
    const calculation = new NavItem({
      id: 'calculation',
      title: 'calculations',
      icon: 'calculation',
      route: Paths.CALC_LYING_ROUND_TIMBER,
      paths: [Paths.CALC_LYING_ROUND_TIMBER],
      permissions: [],
      features: [Feature.WOODWORKING_EXTENDED]
    });

    // ########################################################################
    // #### Statistics
    // ########################################################################
    const statistics = new NavItem({
      id: 'statistics',
      title: 'statistics',
      icon: 'statistics',
      route: Paths.STATISTICS,
      paths: [Paths.STATISTICS],
      permissions: [PermissionScope.STATISTICS]
    });
    // Exports
    statistics.children.push(new NavItem({
      id: 'export',
      title: 'export',
      icon: 'export',
      route: Paths.EXPORTS,
      paths: [Paths.EXPORTS],
      parent: statistics,
      permissions: [PermissionScope.EXPORT]
    }));

    // ########################################################################
    // #### Features
    // ########################################################################
    const feature = new NavItem({
      id: 'feature-main',
      title: 'features',
      icon: 'feature',
      route: Paths.FEATURE_LIST,
      paths: [Paths.FEATURE_LIST],
      permissions: [PermissionScope.FEATURE]
    });
    // Support
    if (this.supportListEnabled) {
      feature.children.push(new NavItem({
        id: 'support-list',
        title: 'support',
        icon: 'support',
        route: Paths.SUPPORT_LIST,
        paths: [Paths.SUPPORT_LIST, Paths.SUPPORT_VIEW],
        parent: feature,
        permissions: [PermissionScope.SUPPORT]
      }));
    }
    // Correspondence
    feature.children.push(new NavItem({
      id: 'correspondence-list',
      title: 'correspondence',
      icon: 'correspondence',
      route: Paths.CORRESPONDENCE_LIST,
      paths: [Paths.CORRESPONDENCE_LIST],
      parent: feature,
      permissions: [PermissionScope.CORRESPONDENCE]
    }));
    // Media
    feature.children.push(new NavItem({
      id: 'media-list',
      title: 'medias',
      icon: 'media',
      route: Paths.MEDIA_LIST,
      paths: [Paths.MEDIA_LIST],
      parent: feature,
      permissions: [PermissionScope.BILLING]
    }));

    // ########################################################################
    // #### Org Wallet Transaction
    // #### Is filtered by NavService.filterNavItemsBySettings (first steps)
    // ########################################################################
    const orgWallet = new NavItem({
      id: 'org-wallet-transaction',
      title: 'org.wallet',
      icon: 'wallet',
      route: Paths.ORG_WALLET_TRANSACTION_LIST,
      paths: [Paths.ORG_WALLET_TRANSACTION_LIST, Paths.ORG_WALLET_PAYMENT_CALLBACK],
      permissions: [PermissionScope.ORG_WALLET]
    });
    // Affiliate
    orgWallet.children.push(new NavItem({
      id: 'affiliate',
      title: 'affiliate.short',
      icon: 'affiliate',
      route: Paths.AFFILIATE,
      paths: [Paths.AFFILIATE],
      parent: orgWallet,
      permissions: [PermissionScope.ORG_WALLET]
    }));

    // ########################################################################
    // #### Settings
    // ########################################################################
    const settings = new NavItem({
      id: 'settings-main',
      title: 'settings',
      icon: 'settings',
      route: Paths.SETTINGS,
      paths: [Paths.SETTINGS],
      permissions: [PermissionScope.SETTINGS]
    });
    // Settings - Org
    const settingOrg = new NavItem({
      id: 'settings-org',
      title: 'org.name',
      icon: 'org',
      route: Paths.SETTINGS_ORG,
      paths: [Paths.SETTINGS_ORG],
      parent: settings,
      permissions: [PermissionScope.SETTINGS, PermissionScope.SETTINGS_ORG]
    });
    settings.children.push(settingOrg);
    // Settings - Org - Numbers
    settingOrg.children.push(new NavItem({
      id: 'settings-numbers',
      title: 'number.name',
      icon: 'number',
      route: Paths.SETTINGS_NUMBERS,
      paths: [Paths.SETTINGS_NUMBERS],
      parent: settingOrg,
      permissions: [PermissionScope.SETTINGS, PermissionScope.SETTINGS_ORG]
    }));
    // Settings - Invoice
    const settingInvoice = new NavItem({
      id: 'settings-invoice',
      title: 'invoices',
      icon: 'invoice',
      route: Paths.SETTINGS_INVOICE,
      paths: [Paths.SETTINGS_INVOICE],
      parent: settings,
      permissions: [PermissionScope.SETTINGS, PermissionScope.SETTINGS_INVOICE]
    });
    settings.children.push(settingInvoice);
    // Settings - Invoice - Text Templates
    settingInvoice.children.push(new NavItem({
      id: 'text-template',
      title: 'text.templates',
      icon: 'template',
      route: Paths.TEXT_TEMPLATE_LIST,
      paths: [Paths.TEXT_TEMPLATE_LIST, Paths.TEXT_TEMPLATE_VIEW, Paths.TEXT_TEMPLATE_EDIT],
      parent: settingInvoice,
      permissions: [PermissionScope.SETTINGS, PermissionScope.SETTINGS_INVOICE]
    }));
    // Settings - Vat-Rate
    settings.children.push(new NavItem({
      id: 'settings-vatrate',
      title: 'vatrates',
      icon: 'vat',
      route: Paths.SETTINGS_VAT_RATE,
      paths: [Paths.SETTINGS_VAT_RATE],
      parent: settings,
      permissions: [PermissionScope.SETTINGS, PermissionScope.SETTINGS_VAT_RATE]
    }));
    // Settings - Vat-Rate
    settings.children.push(new NavItem({
      id: 'settings-contact-vehicle',
      title: 'contact.vehicles',
      icon: 'vehicle',
      route: Paths.SETTINGS_CONTACT_VEHICLE,
      paths: [Paths.SETTINGS_CONTACT_VEHICLE],
      parent: settings,
      permissions: [PermissionScope.SETTINGS, PermissionScope.CONTACT_VEHICLE]
    }));
    // Settings - User
    const settingsUser = new NavItem({
      id: 'settings-user',
      title: 'users',
      icon: 'user',
      route: Paths.SETTINGS_USER_LIST,
      paths: [Paths.SETTINGS_USER_LIST, Paths.SETTINGS_USER_VIEW, Paths.SETTINGS_USER_EDIT],
      parent: settings,
      permissions: [PermissionScope.SETTINGS, PermissionScope.SETTINGS_USER]
    });
    settings.children.push(settingsUser);
    // Settings - User -> Groups
    settingsUser.children.push(new NavItem({
      id: 'settings-group',
      title: 'groups',
      icon: 'group',
      route: Paths.SETTINGS_GROUP_LIST,
      paths: [Paths.SETTINGS_GROUP_LIST, Paths.SETTINGS_GROUP_VIEW, Paths.SETTINGS_GROUP_EDIT],
      parent: settingsUser,
      permissions: [PermissionScope.SETTINGS, PermissionScope.SETTINGS_USER]
    }));
    // Settings - Export
    const settingsExport = new NavItem({
      id: 'settings-export',
      title: 'export',
      icon: 'export',
      route: Paths.SETTINGS_EXPORT,
      paths: [Paths.SETTINGS_EXPORT],
      parent: settings,
      permissions: [PermissionScope.SETTINGS, PermissionScope.EXPORT_DATEV] // Change to 'EXPORT' as soon as more entries are available
    });
    settings.children.push(settingsExport);
    // Settings - Export - DATEV
    settingsExport.children.push(new NavItem({
      id: 'settings-export-datev',
      title: 'export.type.datev',
      icon: 'export',
      route: Paths.SETTINGS_EXPORT_DATEV,
      paths: [Paths.SETTINGS_EXPORT_DATEV],
      parent: settingsExport,
      permissions: [PermissionScope.SETTINGS, PermissionScope.EXPORT_DATEV]
    }));

    if (this.isIronBackupViewer) {
      invoice.children = invoice.children.filter((i) => i.id !== 'invoice-draft');
      contact.children = contact.children.filter((c) => c.id !== 'contact-vehicle-inspection');
      contact.children = contact.children.filter((c) => c.id !== 'contact-tire-change');
      funeral.children = funeral.children.filter((f) => f.id !== 'registry-office');
    }

    const items: NavItem[] = [
      overview,
      funeral,
      invoice,
      receipt,
      contact,
      article,
      calculation,
      statistics,
      feature,
      orgWallet,
      settings
    ];
    return new NavTree(items);
  }
}
